import React from 'react'
import PropTypes from 'prop-types'
import { Title, Box, Icon, Level } from '@deseretbook/react-ui'
import { Review } from './'

/**
 * Filters reviews for most positive / negative criteria
 * Positive = any review with 4 or more stars
 * Negative = any review with 2 or less stars
 *
 * @param {function} filter - additional filter for reviews
 * @param {object} review - product review object
 *
 * @returns {boolean} if all criteria is met
 */
const filterReviews = (filter = () => true) => review => {
  const positiveFeedback = review.positiveFeedback.filter(
    ({ rating }) => rating === 5,
  )
  const negativeFeedback = review.negativeFeedback.filter(
    ({ rating }) => rating === 1,
  )

  if (
    positiveFeedback.length < 2 ||
    negativeFeedback.length > positiveFeedback.length * 2
  ) {
    return false
  }

  return filter(review)
}

/**
 * Sorting function for determining which review is the MOST helpful
 * @param {object} reviewA - The first review from the sorting function array
 * @param {object} reviewB - The next review from the sorting function array
 *
 * @returns {number} either positive negative or 0. Used for array sorting method
 */
const sortByMostHelpful = (reviewA, reviewB) => {
  const positiveFeedbackA = reviewA.feedbackReviews.filter(
    ({ rating }) => rating === 5,
  )
  const positiveFeedbackB = reviewB.feedbackReviews.filter(
    ({ rating }) => rating === 5,
  )

  return positiveFeedbackB.length - positiveFeedbackA.length
}

/**
 * Restructures review object for feedback counts
 *
 * @param {object} review - Product review instance
 *
 * @returns {object} object containing review and positive/negative feedback counts
 */
const reshapeReview = review => ({
  ...review,
  positiveFeedback: review.feedbackReviews.filter(
    feedback => feedback.rating === 5,
  ),
  negativeFeedback: review.feedbackReviews.filter(
    feedback => feedback.rating === 1,
  ),
})

/**
 * Shows most helpful positive/negative review
 * @param {array} reviews - list of product reviews
 * @returns {func} Component
 */
export const MostHelpful = ({ reviews }) => {
  // This array contains all reviews that fit the criteria to be a positive or negative review
  const filteredReviews = reviews
    .map(reshapeReview)
    .filter(filterReviews())
    .sort(sortByMostHelpful)

  /**
   * Grabs the first 5/4 star review from the list of filtered reviews.
   */
  const mostPositiveReview = filteredReviews.find(review => review.rating >= 4)

  /**
   * Grabs the first 2/1 star review from the list of filtered reviews.
   */
  const mostNegativeReview = filteredReviews.find(review => review.rating <= 2)

  return (
    <React.Fragment>
      {mostPositiveReview && (
        <React.Fragment>
          <Box marginVertical={1.5}>
            <Level>
              <Level.Left>
                <Level.Item>
                  <Icon name="thumbUp" shade="light" color="secondary" />
                </Level.Item>
                <Level.Item>
                  <Title
                    title="Most helpful positive review"
                    titleAsComponent="h3"
                    subtitle={`${mostPositiveReview.positiveFeedback.length} Customers found this helpful`}
                  />
                </Level.Item>
              </Level.Left>
            </Level>
            <Review allowFeedback={false} {...mostPositiveReview} />
          </Box>
        </React.Fragment>
      )}
      {mostNegativeReview && (
        <React.Fragment>
          <Box marginVertical={1.5}>
            <Level>
              <Level.Left>
                <Level.Item>
                  <Icon name="thumbDown" shade="light" color="secondary" />
                </Level.Item>
                <Level.Item>
                  <Title
                    title="Most helpful critical review"
                    titleAsComponent="h3"
                    subtitle={`${mostNegativeReview.positiveFeedback.length} Customers found this helpful`}
                  />
                </Level.Item>
              </Level.Left>
            </Level>
            <Review allowFeedback={false} {...mostNegativeReview} />
          </Box>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

MostHelpful.propTypes = {
  reviews: PropTypes.arrayOf(PropTypes.object),
}
